import { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ActivitySquare,
  BookOpenCheck,
  ChevronDown,
  ChevronUp,
  Landmark,
  LogOut,
  MessageSquareMore,
  Settings,
  UserRound
} from 'lucide-react';
import cn from 'classnames';
import { css, StyleSheet } from 'aphrodite';
import { useTranslation } from 'react-i18next';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';
import { getContrastTextColor } from '@utils/helpers/color-utils.ts';
import { useGetMe } from '@utils/api/users/use-get-me.tsx';
import { useLogout } from '@utils/api/users/use-logout.ts';
import { useRoles } from '@utils/hooks/use-roles.ts';
import { PATHS } from '../../../router/paths.ts';
import { IS_ON_PREM } from '@utils/constants/app-constants.ts';
import { useEffect } from 'react';
import { Tooltip } from '@components/atoms/ui-kit/tooltip.tsx';

interface UserRowFooterProps {
  /** When true, render the avatar-only variant used by the editor rail (#2230). */
  collapsed?: boolean;
}

export function UserRowFooter({ collapsed = false }: UserRowFooterProps = {}) {
  const { t } = useTranslation();
  const { getColour, getBrandTint, theme } = useThemeContext();
  const { data: me } = useGetMe();
  const { mutate: logout } = useLogout();
  const navigate = useNavigate();
  const { isSysOp } = useRoles();
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!showMenu) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setShowMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showMenu]);

  const userInitials = useMemo(() => {
    if (!me?.result) return '?';
    const f = me.result.first_name?.[0] ?? '';
    const l = me.result.last_name?.[0] ?? '';
    return (f + l).toUpperCase() || me.result.email[0].toUpperCase();
  }, [me]);

  const userName = useMemo(() => {
    if (!me?.result) return '';
    const full =
      `${me.result.first_name ?? ''} ${me.result.last_name ?? ''}`.trim();
    return full || me.result.email;
  }, [me]);

  const styles = useMemo(
    () =>
      StyleSheet.create({
        menuItem: {
          color: getColour('normalText'),
          transition: 'background-color 0.12s ease',
          ':hover': {
            backgroundColor: getBrandTint(0.06),
            color: getColour('activeText')
          }
        },
        menuItemDestructive: {
          color: getColour('normalText'),
          transition: 'background-color 0.12s ease',
          ':hover': {
            backgroundColor: 'rgba(239,68,68,0.08)',
            color: 'rgba(239,68,68,1)'
          }
        }
      }),
    [getColour, getBrandTint, theme]
  );

  const menuItemClass = (extra?: string) =>
    cn(
      'flex items-center gap-3 px-4 py-2.5 text-sm font-medium w-full text-left',
      css(styles.menuItem),
      extra
    );

  return (
    <div
      ref={menuRef}
      className={cn('relative', collapsed ? 'py-0' : 'pt-2 pb-1')}
    >
      {showMenu && (
        <div
          className={cn(
            'absolute bottom-full mb-2 w-56 rounded-2xl py-1 z-50',
            collapsed ? 'left-full ml-2 bottom-0 mb-0' : 'right-0'
          )}
          style={{
            backgroundColor: getColour('thirdLayer'),
            boxShadow:
              '0 -4px 24px -4px rgba(0,0,0,0.18), 0 0 0 1px ' +
              getColour('hairline')
          }}
        >
          {isSysOp && !IS_ON_PREM && (
            <a
              href="/back-office/customers"
              onClick={() => setShowMenu(false)}
              className={menuItemClass()}
            >
              <Landmark size={16} className="shrink-0" />
              {t('back-office')}
            </a>
          )}
          {IS_ON_PREM && (
            <button
              onClick={() => {
                navigate(PATHS().ADMIN_DASHBOARD);
                setShowMenu(false);
              }}
              className={menuItemClass()}
            >
              <Settings size={16} className="shrink-0" />
              {t('admin-panel')}
            </button>
          )}
          {!IS_ON_PREM && (
            <button
              onClick={() => {
                navigate(PATHS().STATUS);
                setShowMenu(false);
              }}
              className={menuItemClass()}
            >
              <ActivitySquare size={16} className="shrink-0" />
              {t('status')}
            </button>
          )}
          {!IS_ON_PREM && (
            <a
              href="https://feedback.scriptix.io/"
              target="_blank"
              rel="noreferrer noopener"
              onClick={() => setShowMenu(false)}
              className={menuItemClass()}
            >
              <MessageSquareMore size={16} className="shrink-0" />
              {t('feedback')}
            </a>
          )}
          <a
            href="https://docs.scriptix.io/"
            target="_blank"
            rel="noreferrer noopener"
            onClick={() => setShowMenu(false)}
            className={menuItemClass()}
          >
            <BookOpenCheck size={16} className="shrink-0" />
            {t('docs')}
          </a>
          <div
            className="my-1 mx-3 h-px"
            style={{ backgroundColor: getColour('hairline') }}
          />
          <button
            onClick={() => {
              navigate(PATHS().PROFILE);
              setShowMenu(false);
            }}
            className={menuItemClass()}
          >
            <UserRound size={16} className="shrink-0" />
            {t('profile')}
          </button>
          <button
            onClick={() => {
              logout({ isManual: true });
              setShowMenu(false);
            }}
            className={cn(
              'flex items-center gap-3 px-4 py-2.5 text-sm font-medium w-full text-left',
              css(styles.menuItemDestructive)
            )}
          >
            <LogOut size={16} className="shrink-0" />
            {t('log-out')}
          </button>
        </div>
      )}

      {collapsed ? (
        <Tooltip label={userName || t('open-menu')} placement="right">
          <button
            onClick={() => setShowMenu(v => !v)}
            className="inline-flex h-9 w-9 items-center justify-center rounded-full text-xs font-bold shadow-sm transition-all duration-150 hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary"
            style={{
              backgroundColor: getColour('activeText'),
              color: getContrastTextColor(getColour('activeText'))
            }}
            aria-label={t('open-menu')}
            aria-expanded={showMenu}
          >
            {userInitials}
          </button>
        </Tooltip>
      ) : (
        <button
          onClick={() => setShowMenu(v => !v)}
          className="w-full flex items-center gap-3 px-2 py-2 rounded-xl transition-all duration-150"
          style={{
            backgroundColor: getBrandTint(0.1),
            border: `1px solid ${getBrandTint(0.2)}`
          }}
          aria-label={t('open-menu')}
          aria-expanded={showMenu}
        >
          <div
            className="h-9 w-9 rounded-full flex items-center justify-center text-sm font-bold shrink-0 shadow-sm"
            style={{
              backgroundColor: getColour('activeText'),
              color: getContrastTextColor(getColour('activeText'))
            }}
          >
            {userInitials}
          </div>
          <div className="flex-1 min-w-0 text-left">
            <p
              className="text-sm font-semibold truncate leading-tight"
              style={{ color: getColour('normalText') }}
            >
              {userName}
            </p>
            {me?.result?.email && userName !== me.result.email && (
              <p
                className="text-xs truncate leading-tight"
                style={{ color: getColour('inactiveText') }}
              >
                {me.result.email}
              </p>
            )}
          </div>
          {showMenu ? (
            <ChevronUp
              size={15}
              className="shrink-0"
              style={{ color: getColour('activeText') }}
            />
          ) : (
            <ChevronDown
              size={15}
              className="shrink-0"
              style={{ color: getColour('activeText') }}
            />
          )}
        </button>
      )}
    </div>
  );
}
